/**
 * CoachPlaybook - Learn Page
 * Version: 0.6.0
 * Description: Interactive learning hub — browse lessons, take quizzes, learn plays
 */

import React, { useState, useEffect } from 'react';
import { getPlaysBySport } from '../../utils/db';
import { useSport } from '../common/SportContext';

// Lazy imports for quiz components (may not be ready yet)
let QuizPlayer = null;
let LessonLibrary = null;
try {
  QuizPlayer = require('./QuizPlayer').default;
  LessonLibrary = require('./LessonLibrary').default;
} catch (e) {
  // Components not yet available
}

const LearnPage = ({ onNavigate }) => {
  const [view, setView] = useState('library'); // 'library' | 'quiz'
  const [selectedPlay, setSelectedPlay] = useState(null);
  const [plays, setPlays] = useState([]);
  const [quizResults, setQuizResults] = useState(null);
  const { sportConfig, currentSport } = useSport();

  useEffect(() => {
    const loadPlays = async () => {
      const filtered = await getPlaysBySport(currentSport);
      setPlays(filtered);
    };
    loadPlays();
  }, [currentSport]);

  const handleStartQuiz = (play) => {
    setSelectedPlay(play);
    setQuizResults(null);
    setView('quiz');
  };

  const handleQuizComplete = (results) => {
    setQuizResults(results);
    setView('results');
  };

  const handleBackToLibrary = () => {
    setSelectedPlay(null);
    setQuizResults(null);
    setView('library');
  };

  // Results view
  if (view === 'results' && quizResults) {
    return (
      <div className="h-full overflow-y-auto bg-slate-900">
        <div className="p-6 md:p-10 pb-24">
          <button
            onClick={handleBackToLibrary}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Lessons
          </button>

          <div className="text-center py-12">
            <div className="text-8xl mb-6">
              {quizResults.accuracy >= 80 ? '🏆' : quizResults.accuracy >= 60 ? '👍' : '📚'}
            </div>
            <h2 className="text-4xl font-black text-white mb-4">
              {quizResults.accuracy >= 80 ? 'Excellent!' : quizResults.accuracy >= 60 ? 'Good Work!' : 'Keep Practicing!'}
            </h2>
            <div className="text-6xl font-black text-cyan-400 mb-2">
              {quizResults.accuracy}%
            </div>
            <p className="text-slate-400 text-lg mb-8">
              {quizResults.correct} of {quizResults.total} correct
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => { setQuizResults(null); setView('quiz'); }}
                className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-xl transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleBackToLibrary}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-colors"
              >
                Browse More
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz view
  if (view === 'quiz' && selectedPlay && QuizPlayer) {
    return (
      <div className="h-full overflow-y-auto bg-slate-900">
        <div className="p-4">
          <button
            onClick={handleBackToLibrary}
            className="mb-4 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Exit Quiz
          </button>
          <QuizPlayer
            play={selectedPlay}
            sport={sportConfig}
            onComplete={handleQuizComplete}
          />
        </div>
      </div>
    );
  }

  // Library view (default)
  if (LessonLibrary) {
    return (
      <div className="h-full overflow-y-auto bg-slate-900">
        <div className="p-6 md:p-10 pb-24">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight flex items-center gap-3">
              <span className="text-4xl">🎓</span>
              Learn
            </h1>
            <p className="text-slate-400 text-lg mt-1">
              Master plays through interactive quizzes
            </p>
          </div>
          <LessonLibrary
            plays={plays}
            sports={sportConfig ? [sportConfig] : []}
            onStartQuiz={handleStartQuiz}
          />
        </div>
      </div>
    );
  }

  // Fallback if components not loaded
  return (
    <div className="h-full overflow-y-auto bg-slate-900">
      <div className="p-6 md:p-10 pb-24">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight flex items-center gap-3">
            <span className="text-4xl">🎓</span>
            Learn
          </h1>
          <p className="text-slate-400 text-lg mt-1">
            Master plays through interactive quizzes
          </p>
        </div>
        <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-bold text-white mb-2">Create Plays First</h3>
          <p className="text-slate-400 mb-6">Design plays in the Playbook, then come here to quiz yourself on them.</p>
          <button
            onClick={() => onNavigate('plays')}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-colors"
          >
            Go to Playbook
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearnPage;
